import { useEffect, useState } from 'react';

import { Popconfirm } from 'antd';
import { CircleAlert, FileText, LoaderCircle, RefreshCw, Trash2 } from 'lucide-react';

import { STATUS } from '../constants';
import { formatElapsed, removeSection } from '../helpers';

import styles from '../index.module.scss';

function ElapsedTime() {
    const [start] = useState(() => Date.now());
    const [now, setNow] = useState(start);

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    return <>{formatElapsed(now - start)}</>;
}

function TitleEditor({ title, disabled, onCommit }) {
    const [draft, setDraft] = useState(title || '');
    const commit = () => {
        const value = draft.trim();
        if (!value) {
            setDraft(title || '');
            return;
        }
        if (value !== title) onCommit(value);
    };
    return (
        <div className={styles.field}>
            <label htmlFor="ppt-material-title">资料标题</label>
            <input
                id="ppt-material-title"
                className={styles.input}
                value={draft}
                maxLength={200}
                disabled={disabled}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={commit}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') event.currentTarget.blur();
                }}
            />
        </div>
    );
}

function FileHeader({ task, detail, children }) {
    return (
        <div className={styles.materialHead}>
            <span className={styles.materialIcon} aria-hidden>
                <FileText size={18} />
            </span>
            <div className={styles.materialName}>
                <strong title={task.fileName}>{task.fileName}</strong>
                <span>{detail}</span>
            </div>
            {children ? <div className={styles.materialActions}>{children}</div> : null}
        </div>
    );
}

export default function MaterialPanel({ task, saving, onRebuild, onReplace, onSave }) {
    const chars = task.sourceChars ? `${task.sourceChars.toLocaleString()} 字` : '';

    if (task.status === STATUS.MATERIAL_PROCESSING) {
        const { done = 0, total = 0 } = task.materialState || {};
        return (
            <div className={styles.materialCard}>
                <FileHeader
                    task={task}
                    detail={[chars, '正在整理资料'].filter(Boolean).join(' · ')}
                >
                    <button type="button" className={styles.textButton} onClick={onReplace}>
                        换一个文件
                    </button>
                </FileHeader>
                <div className={styles.materialProgress} role="status" aria-live="polite">
                    <LoaderCircle className={styles.spin} size={18} aria-hidden />
                    <div>
                        <p>
                            {total > 1
                                ? `文档较长，已按章节分成 ${total} 部分整理，完成 ${done} 部分`
                                : '正在提炼章节要点，并摘录关键数字和原文出处'}
                        </p>
                        <small>
                            已等待 <ElapsedTime />
                            。通常需要 1–3
                            分钟，可以先去选模板并确认支付，整理完成后会自动开始生成。
                        </small>
                    </div>
                </div>
            </div>
        );
    }

    if (task.status === STATUS.MATERIAL_FAILED) {
        return (
            <div className={styles.materialCard} data-tone="error">
                <FileHeader task={task} detail={chars} />
                <div className={styles.materialError} role="alert">
                    <CircleAlert size={16} aria-hidden />
                    <p>{task.materialState?.error || '资料整理失败，请重新整理'}</p>
                </div>
                <div className={styles.materialFooter}>
                    <button type="button" className={styles.secondaryButton} onClick={onReplace}>
                        换一个文件
                    </button>
                    <button type="button" className={styles.primaryButton} onClick={onRebuild}>
                        <RefreshCw size={15} aria-hidden />
                        重新整理
                    </button>
                </div>
            </div>
        );
    }

    const material = task.material;
    if (!material) return null;
    const sections = material.sections || [];
    const evidenceCount = material.evidence?.length || 0;

    return (
        <div className={styles.materialCard}>
            <FileHeader
                task={task}
                detail={`${sections.length} 个章节 · ${evidenceCount} 条原文证据 · 已整理完成`}
            >
                <button
                    type="button"
                    className={styles.textButton}
                    disabled={saving}
                    onClick={onRebuild}
                >
                    <RefreshCw size={14} aria-hidden />
                    重新整理
                </button>
                <button
                    type="button"
                    className={styles.textButton}
                    disabled={saving}
                    onClick={onReplace}
                >
                    换文件
                </button>
            </FileHeader>

            {task.status === STATUS.SUBMIT_FAILED && task.error?.message ? (
                <div className={styles.materialError} role="alert">
                    <CircleAlert size={16} aria-hidden />
                    <p>上次提交失败：{task.error.message}，调整后可重新生成。</p>
                </div>
            ) : null}
            {task.status === STATUS.AWAITING_PAYMENT ? (
                <div className={styles.materialProgress} role="status">
                    <CircleAlert size={16} aria-hidden />
                    <p>这份资料已确认过生成选项、尚未支付；可以调整后重新确认生成。</p>
                </div>
            ) : null}

            <TitleEditor
                key={material.title}
                title={material.title}
                disabled={saving}
                onCommit={(title) => onSave({ ...material, title })}
            />

            {material.summary ? (
                <details className={styles.materialSummary}>
                    <summary>全文摘要</summary>
                    <p>{material.summary}</p>
                </details>
            ) : null}

            <ol className={styles.sectionList} aria-label="整理出的章节">
                {sections.map((section, index) => {
                    const points = section.key_points?.length || 0;
                    const refs = section.evidence_ids?.length || 0;
                    return (
                        <li key={section.id} className={styles.sectionItem}>
                            <span className={styles.sectionIndex} aria-hidden>
                                {index + 1}
                            </span>
                            <div className={styles.sectionBody}>
                                <strong>{section.title}</strong>
                                <p>{section.summary}</p>
                                <small>
                                    {points ? `${points} 个要点` : '无补充要点'}
                                    {refs ? ` · 引用 ${refs} 条原文证据` : ''}
                                </small>
                            </div>
                            <Popconfirm
                                title="删除这一节？"
                                description="生成 PPT 时不再使用这一节的内容"
                                okText="删除"
                                cancelText="取消"
                                disabled={sections.length <= 1 || saving}
                                onConfirm={() => onSave(removeSection(material, section.id))}
                            >
                                <button
                                    type="button"
                                    className={styles.iconButton}
                                    aria-label={`删除章节：${section.title}`}
                                    disabled={sections.length <= 1 || saving}
                                >
                                    <Trash2 size={15} aria-hidden />
                                </button>
                            </Popconfirm>
                        </li>
                    );
                })}
            </ol>
            <p className={styles.materialNote}>
                资料只取自你上传的文档，数字和出处以原文为准；目录、参考文献、致谢等不会整理成章节。
            </p>
        </div>
    );
}
